import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Spinner, Container } from 'react-bootstrap';
import AddClubDetails from '../AddClubDetails';
import TeamListingSection from './TeamListingSection';
import LoginCredentials from './LoginCredentials';
import { getClubDetails, updateClubDetails } from '../../../api/club/clubDetailsService';
import { deleteClub } from '../../../api/club/allClubsService';
import { isSuperAdmin } from '../../../utils/roleBasedAccess';
import { DeleteModal } from '../../../Common/DeleteModal';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import './ClubDetails.css';
import { getImageUrl } from '../../../utils/imageUrl';

interface ClubDetailsData {
  _id: string;
  name: string;
  logo: string;
  emailId: string;
  managerName: string;
  countryCode: number;
  managerContactNum: number;
  location: string;
  isActive: boolean;
  // ✅ NEW FIELDS
  address?: string;
  gstNum?: string;  // API uses gstNum
}

const ClubDetails: React.FC = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  
  const [clubData, setClubData] = useState<ClubDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const location = useLocation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);

  // Logo download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Get user data for permission check
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData = {
    ...user,
    subRole: user.subRole || role,
    permissions: permissions
  };
  const isSuperAdminUser = isSuperAdmin(userData);

  // Fetch club details
  const fetchClubDetails = async () => {
    if (!clubId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getClubDetails(clubId);
      setClubData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch club details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClubDetails();
  }, [clubId]);

  // Check if we're in edit mode based on URL query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get('mode');
    setIsEditMode(mode === 'edit');
  }, [location.search]);

  // Handle club update
  const handleClubUpdate = async (updatedData: any) => {
    if (!clubId) return;
    
    try {
      setIsUpdating(true);
      await updateClubDetails(clubId, updatedData);
      await fetchClubDetails(); // Refresh data
      setIsEditMode(false);
      setHasFormChanges(false); // Reset form changes after successful update
    } catch (err: any) {
      setError(err.message || 'Failed to update club details');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle form changes
  const handleFormChange = (hasChanges: boolean) => {
    setHasFormChanges(hasChanges);
  };

  // Handle delete club - show modal
  const handleDeleteClub = () => {
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  // Confirm delete club - actual deletion
  const confirmDeleteClub = async () => {
    if (!clubId) return;
    
    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      await deleteClub(clubId);
      
      // Close modal and navigate back to all clubs
      setShowDeleteModal(false);
      navigate('/all-clubs');
    } catch (err: any) {
      console.error('Failed to delete club:', err);
      setDeleteError(err.message || 'Failed to delete club. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteError(null);
  };

  // Helper function to get logo URL
  const getLogoUrl = (logoPath: string): string => {
    if (!logoPath) return '';
    if (logoPath.startsWith('http')) return logoPath;
    return getImageUrl(logoPath);
  };

  // Handle logo download for super admins
  const handleLogoDownload = async () => {
    if (!clubData?.logo || !isSuperAdminUser) return;
    
    try {
      setIsDownloading(true);
      setDownloadError(null);
      
      // Construct the full S3 URL
      const logoUrl = getLogoUrl(clubData.logo);
      
      // Fetch the image
      const response = await fetch(logoUrl);
      if (!response.ok) throw new Error('Failed to fetch logo');
      
      const blob = await response.blob();
      
      // Extract filename from the logo path or create a default one
      const filename = clubData.logo.includes('/') 
        ? clubData.logo.split('/').pop() 
        : `club_logo_${clubData.name || 'download'}.${blob.type.split('/')[1] || 'jpg'}`;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'club_logo.jpg';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      console.error('Logo download error:', error);
      setDownloadError('Failed to download logo. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid>
        <ShimmerLoader type="club-details" />
      </Container>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!clubData) {
    return (
      <div className="alert alert-warning" role="alert">
        Club not found
      </div>
    );
  }

  return (
    <div className="club-details-page">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={[
            { label: 'All Clubs', onClick: () => navigate('/all-clubs') },
            { label: 'Club Details', active: true }
          ]} />
        </div>
        
        {/* Header */}
        <div className="page-header-section mb-4" style={{ padding: '0 15px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px', marginBottom: '24px' }}>
            <button 
              className="back-button"
              onClick={() => navigate('/all-clubs')}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            {/* Show club logo if provided */}
            {clubData.logo && (
            <div className="club-logo-small" style={{ flexShrink: 0 }}>
                <img 
                  src={getLogoUrl(clubData.logo)}
                  alt="Club Logo"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.nextElementSibling?.classList.remove('d-none');
                  }}
                />
                <div className="default-club-logo-small d-none">
                  <i className="ri-building-line"></i>
                </div>
                
                {/* Add download button for super admins */}
                {isSuperAdminUser && (
                  <div className="logo-download-action">
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={handleLogoDownload}
                      disabled={isDownloading}
                      className="logo-download-btn"
                      title="Download Club Logo"
                    >
                      {isDownloading ? (
                        <i className="ri-loader-4-line spin"></i>
                      ) : (
                        <i className="ri-download-line"></i>
                      )}
                    </Button>
                  </div>
                )}
                </div>
              )}
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <h1 className="page-title mb-0">{clubData.name}</h1>
            </div>
          </div>
        </div>

        {/* Download Error Display */}
        {downloadError && (
          <div style={{ padding: '0 15px' }}>
            <div className="alert alert-danger" role="alert">
              <i className="ri-error-warning-line me-2"></i>
              {downloadError}
            </div>
          </div>
        )}

        {/* Club Details Section */}
        <div style={{ padding: '0 15px' }}>
          <AddClubDetails 
            isViewMode={!isEditMode}
            isEditMode={isEditMode}
            initialData={clubData}
            onUpdate={handleClubUpdate}
            onCancel={() => setIsEditMode(false)}
            showBreadcrumbs={false}
            showHeader={false}
            hideLoginCredentials={true}
            containerPadding={false}
            actionButtons={
              !isEditMode ? (
                <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                  <Button 
                    variant="danger" 
                    size="sm"
                    onClick={handleDeleteClub}
                    title="Delete Club"
                  >
                    <i className="ri-delete-bin-line me-1"></i>
                    Delete
                  </Button>
                  <Button 
                    variant="dark" 
                    size="sm"
                    onClick={() => setIsEditMode(true)}
                    title="Edit Club Details"
                  >
                    <i className="ri-edit-line me-1"></i>
                    Edit
                  </Button>
                </div>
              ) : (
                <>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setIsEditMode(false)}
                    className="me-2"
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm"
                    disabled={isUpdating || !hasFormChanges}
                    onClick={() => {
                      // Trigger form submission
                      const form = document.querySelector('form');
                      if (form) {
                        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                        form.dispatchEvent(submitEvent);
                      }
                    }}
                  >
                    {isUpdating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </>
              )
            }
            onFormChange={handleFormChange}
          />
        </div>

        {/* Login Credentials Card */}
        <div style={{ padding: '0 15px' }}>
          <LoginCredentials 
            clubId={clubId!}
            initialEmail={clubData.emailId}
            isViewMode={!isEditMode}
          />
        </div>

        {/* Team Listing Section */}
            <div style={{ padding: '0 15px' }}>
              <TeamListingSection clubId={clubId!} />
            </div>
      </Container>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        deleteModalFunction={confirmDeleteClub}
        title="Delete Club"
        message={`Are you sure you want to delete "${clubData.name}"? This action cannot be undone and will remove all associated teams, players, and data.`}
        confirmLabel="Delete Club"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />
    </div>
  );
};

export default ClubDetails; 